import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

function CommentSection({ cardId }) {
  const queryClient = useQueryClient()
  const [newMessage, setNewMessage] = useState('')

  const commentsQuery = useQuery({
    queryKey: ['kanbanCardComments', cardId],
    queryFn: async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('get', `/kanban_cards/${cardId}/comments`, {
        headers: { Accept: 'application/json' }
      })
      const response = await request.perform()
      return response.json
    }
  })

  const createComment = useMutation({
    mutationFn: async (message) => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('post', `/kanban_cards/${cardId}/comments`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ message })
      })
      const response = await request.perform()
      return response.json
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbanCardComments', cardId] })
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!newMessage.trim()) return
    createComment.mutate(newMessage.trim())
    setNewMessage('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const comments = commentsQuery.data?.comments || []

  const formatDate = (dateStr) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }

  return (
    <div className="flex flex-col" style={{ maxHeight: '300px' }}>
      <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
        {commentsQuery.isLoading && (
          <div className="text-xs text-gray-400 py-2">Loading comments...</div>
        )}

        {!commentsQuery.isLoading && comments.length === 0 && (
          <div className="text-xs text-gray-400 py-2">No comments yet</div>
        )}

        {comments.map(comment => (
          <div key={comment.id} className="py-2 border-b border-gray-100 last:border-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-700">{comment.user_name}</span>
              <span className="text-xs text-gray-400">{formatDate(comment.created_at)}</span>
            </div>
            <p className="text-sm text-gray-600">{comment.message}</p>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="mt-2 pt-2 border-t border-gray-200">
        <div className="flex gap-2">
          <textarea
            className="form-textarea flex-1 rounded-md border-gray-300 shadow-sm text-sm"
            rows="2"
            placeholder="Add a comment..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            type="submit"
            className="inline-flex justify-center rounded-md px-4 py-2 bg-blue-100 text-sm leading-5 font-semibold text-blue-700 hover:bg-blue-50 active:bg-blue-200 cursor-pointer self-end"
            disabled={!newMessage.trim() || createComment.isPending}
          >
            {createComment.isPending ? '...' : 'Post'}
          </button>
        </div>
      </form>
    </div>
  )
}

CommentSection.propTypes = {
  cardId: PropTypes.number.isRequired
}

export default CommentSection
